import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Package, 
  Check,
  ArrowRight
} from 'lucide-react';
import { packsApiService, Pack } from '../services/packsApi';

const PacksPage: React.FC = () => {
  const navigate = useNavigate();
  const [packs, setPacks] = useState<Pack[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPacks();
  }, []);

  const loadPacks = async () => {
    try {
      // Récupérer les packs actifs depuis l'API
      const packsData = await packsApiService.getPacks(true);
      setPacks(packsData);
    } catch (error) {
      console.error('Erreur lors du chargement des packs:', error);
      // En cas d'erreur, afficher un tableau vide
      setPacks([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des packs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/80 backdrop-blur-sm border-b border-gray-100/50 sticky top-0 z-40"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Retour à l'accueil</span>
            </button>
            
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900">Nos Packs Premium</h1>
              <p className="text-gray-600">Solutions complètes pour votre réussite digitale</p>
            </div>
            
            <div className="w-32"></div>
          </div>
        </div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <Package className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Packs <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">Premium</span>
          </h1>
          
          <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto leading-relaxed">
            Découvrez nos solutions complètes conçues pour accélérer votre transformation digitale. 
            Chaque pack combine expertise, innovation et accompagnement personnalisé.
          </p>
        </motion.div>

        {/* Grille des packs */}
        {packs.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun pack disponible</h3>
            <p className="text-gray-600">Les packs seront bientôt disponibles.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-16">
            {packs.map((pack, index) => (
              <motion.div
                key={pack.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative rounded-3xl p-8 shadow-xl border bg-white/80 backdrop-blur-sm border-white/50 transition-all duration-300 hover:shadow-2xl hover:scale-105"
              >
                {/* Header */}
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Package className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{pack.name}</h3>
                  <p className="text-gray-700 leading-relaxed">{pack.description}</p>
                </div>

                {/* Prix */}
                <div className="text-center mb-8">
                  <div className="text-4xl font-bold bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
                    {pack.total_price.toLocaleString()} FCFA
                  </div>
                  {pack.payment_facility && (
                    <div className="text-sm text-green-600 font-semibold mt-1">
                      {pack.payment_facility}
                    </div>
                  )}
                </div>

                {/* Services inclus */}
                {pack.services && pack.services.length > 0 && (
                  <div className="space-y-4 mb-8">
                    <h4 className="font-semibold text-gray-900 mb-3">Services inclus :</h4>
                    {pack.services.map((service, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                        <div>
                          <span className="text-gray-900 font-medium">{service.name}</span>
                          {service.description && (
                            <p className="text-gray-600 text-sm mt-1">{service.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Bouton d'action */}
                <button
                  onClick={() => navigate('/devisrequest', { state: { selectedPack: pack } })}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-4 px-6 rounded-2xl font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-2"
                >
                  <span>Choisir ce pack</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </motion.div>
            ))}
          </div>
        )}

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-white shadow-2xl">
            <h2 className="text-3xl font-bold mb-6">Besoin d'une solution sur mesure ?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Nos packs ne correspondent pas exactement à vos besoins ? 
              Contactez-nous pour créer une solution parfaitement adaptée à votre projet.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/devisrequest')}
                className="bg-white text-blue-600 px-8 py-4 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Demander un devis personnalisé
              </button>
              <button 
                onClick={() => navigate('/services')}
                className="border-2 border-white text-white px-8 py-4 rounded-2xl font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300"
              >
                Voir nos services individuels
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PacksPage;
